import MStatic from 'mithril/hyperscript';
import type { IMithrilMountableComponent } from './components/mithril.component';

/**
 * FIXME: Move this definition into core-shared so that it can be used for SVG Renderer components
 */
export type MithrilVNode = CustomMithrilVNode | null;

export interface CustomMithrilVNode<T = any> {
	attrs: T;
	children: MithrilVNode[];
	dom: HTMLElement;
	text?: string;
}

export interface MConfig {
	/** Class name(s) to apply */
	className?: string;
	/** Inline style(s) to apply. */
	style?: string | object;
	/**
	 * (Mithril) Unique identifier field of the objects in the data array.
	 *
	 * Having a key means that if the data array is shuffled and the view is re-rendered, the inputs will be shuffled in the exact same order, so as to maintain correct focus and DOM state.
	 *
	 * @see https://mithril.js.org/hyperscript.html#keys
	 */
	key?: string;
	/**
	 * (DOM) The click event is raised when the user clicks on an element. It fires after the mousedown and mouseup events, in that order.
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/GlobalEventHandlers/onclick
	 */
	onclick?: (ev: MouseEvent) => any;
	/**
	 * (DOM) Fired when the value of an element has been "committed".
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/GlobalEventHandlers/onchange
	 */
	onchange?: (ev: Event) => any;
	/**
	 * (DOM) Fired for each value alteration for a <input> or <textarea> element.
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/GlobalEventHandlers/oninput
	 */
	oninput?: (ev: Event) => any;
	/**
	 * (Mithril) Called after a DOM element is created and attached to the document.
	 * @see https://mithril.js.org/lifecycle-methods.html#oncreate
	 */
	oncreate?: (vnode: CustomMithrilVNode) => any;
	/**
	 * (Mithril) Called after a DOM element is updated, while attached to the document.
	 * @see https://mithril.js.org/lifecycle-methods.html#onupdate
	 */
	onupdate?: (vnode: CustomMithrilVNode) => any;
	/**
	 * (Mithril) Called before a DOM element is removed from the document.
	 * @see https://mithril.js.org/lifecycle-methods.html#onremove
	 */
	onremove?: (vnode: CustomMithrilVNode) => any;
	[x: string]: any;
}

export type MChild = MithrilVNode | string | number;

export interface MType {
	/**
	 * Represents an HTML element in a Mithril view.
	 * @see https://mithril.js.org/hyperscript.html
	 */
	(tag: string, config?: MConfig, children?: MChild | MChild[]): MithrilVNode;
	(tag: string, children?: MChild | MChild[]): MithrilVNode;

	/**
	 * Allows attaching lifecycle methods to a fragment vnode.
	 * @see https://mithril.js.org/fragment.html
	 */
	fragment: (config: MConfig, children?: MChild[]) => MithrilVNode;

	/**
	 * Turns an HTML or SVG string into unescaped HTML or SVG. Do not use m.trust on unsanitized user input.
	 * @see https://mithril.js.org/trust.html
	 */
	trust: (content: string) => MithrilVNode;
}

export const m: MType = MStatic;

/**
 * A non-component object that defines how a route should be handled in Mithril.
 *
 * Only one of `onmatch` or `render` must be defined.
 *
 * @see https://mithril.js.org/route.html#routeresolver
 */
export interface IMithrilRouteResolver {
	onmatch?(
		params: { [name: string]: string },
		path: string
	): IMithrilMountableComponent | Promise<IMithrilMountableComponent>;
	render?(vnode: MithrilVNode): MithrilVNode | MithrilVNode[];
}
